import { openAddOnsStoreModal } from "src/js/stores/navigation/actions";
import { ComparablePriceCard } from "./ComparablePriceCard";
import { ComparableDetailSection } from "./ComparableDetailSection";
import { IComparablesTransactionDTO } from "react-migration/lib/typings/comparables/Comparables";
import { ZooplaComparable } from "react-migration/lib/typings/Zoopla";
import { MeasurementSystem } from "src/js/stores/user/store";
import { getMeasurementSystemPreference } from "src/js/stores/user/actions/getSetting";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import {
  PriceSource,
  useComparableDetailData,
} from "react-migration/domains/comparables/hooks/useComparableDetailData";
import { SkeletonLoading } from "react-migration/components/SkeletonLoading";
import { DataPanel } from "react-migration/components/DataPanel";
import { ZooplaListingsDTO } from "react-migration/domains/comparables/typings/Zoopla";
import { HOMETRACK_ADDON_ROUTE } from "react-migration/domains/nav/components/Modals/AddOnsStoreModal/constants";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";

export const ComparablePriceSection = ({
  record,
  zooplaData,
  loading,
  listingsData,
  noFullAccess,
}: {
  record?: IComparablesTransactionDTO;
  zooplaData?: ZooplaComparable;
  loading?: boolean;
  listingsData?: ZooplaListingsDTO;
  noFullAccess: boolean;
}) => {
  const { t } = useTranslation();
  const unitPreference = getMeasurementSystemPreference();
  const isMetric = unitPreference === MeasurementSystem.METRIC;
  const isUSUser = hasFeature(Feature.usAccess);

  const {
    indexedOrEstimateCurrentValue,
    indexedOrEstimateCurrentValuePerArea,
    indexedOrEstimateValueSource,

    lastKnownValue,
    lastKnownValueSource,
    lastKnownValueDate,
    lastKnownValueType,

    estimateRentalValue,
    estimateRentalValuePerArea,

    lastRentalValue,
    lastRentalValueDate,
    lastRentalValueType,
  } = useComparableDetailData(noFullAccess, record, zooplaData, listingsData);

  if (loading) return <SkeletonPriceSection />;

  const perAreaUnit = `/${t(isMetric ? "comparables.sqm.alt" : "comparables.sqft.alt")}`;

  return (
    <div className="w-full flex flex-col items-start justify-between gap-y-4">
      <PriceSection title={t("comparables.selection_details.property_values_section.sale")}>
        {indexedOrEstimateValueSource === PriceSource.HMLR && (
          <ComparablePriceCard
            title={t("comparables.selection_details.estimate_market_value")}
            mainData={indexedOrEstimateCurrentValue}
            auxiliaryData={indexedOrEstimateCurrentValuePerArea}
            auxiliaryDataSubordinate={indexedOrEstimateCurrentValuePerArea ? perAreaUnit : null}
            tooltip={t("comparables.filters.displayBy.est_price.description")}
          />
        )}
        {lastKnownValueSource === PriceSource.HMLR && (
          <ComparablePriceCard
            title={t("comparables.filters.displayBy.price")}
            mainData={lastKnownValue}
            auxiliaryData={lastKnownValueType}
            auxiliaryDataSubordinate={lastKnownValueDate}
            tooltip={t("comparables.filters.displayBy.price.description")}
          />
        )}

        {indexedOrEstimateValueSource === PriceSource.HOMETRACK && (
          <ComparablePriceCard
            title={t("comparables.filters.displayBy.zoopla_valuation_current")}
            mainData={indexedOrEstimateCurrentValue}
            auxiliaryData={indexedOrEstimateCurrentValuePerArea}
            auxiliaryDataSubordinate={indexedOrEstimateCurrentValuePerArea ? perAreaUnit : null}
            tooltip={t("comparables.filters.displayBy.zoopla_valuation_current.description")}
          />
        )}
        {lastKnownValueSource === PriceSource.HOMETRACK && (
          <ComparablePriceCard
            title={t("comparables.selection_details.last_known_value")}
            mainData={lastKnownValue}
            auxiliaryData={lastKnownValueType}
            auxiliaryDataSubordinate={lastKnownValueDate}
            tooltip={t("comparables.selection_details.last_known_value.tooltip")}
          />
        )}
      </PriceSection>

      {!isUSUser && (
        <PriceSection title={t("comparables.selection_details.property_values_section.rent")}>
          <ComparablePriceCard
            title={t("comparables.filters.displayBy.zoopla_rental_estimate")}
            mainData={estimateRentalValue}
            auxiliaryData={estimateRentalValuePerArea}
            auxiliaryDataSubordinate={estimateRentalValuePerArea ? perAreaUnit : null}
            tooltip={t("comparables.filters.displayBy.zoopla_rental_estimate.description")}
            disabled={noFullAccess}
            onClick={
              noFullAccess
                ? () => {
                    openAddOnsStoreModal({
                      path: HOMETRACK_ADDON_ROUTE,
                      logClickSource: "zoopla-preview",
                    });
                  }
                : undefined
            }
          />
          <ComparablePriceCard
            title={t("comparables.filters.displayBy.zoopla_rental_asking")}
            mainData={lastRentalValue}
            auxiliaryData={lastRentalValueType}
            auxiliaryDataSubordinate={lastRentalValueDate}
            tooltip={t("comparables.filters.displayBy.zoopla_rental_asking.description")}
            disabled={noFullAccess}
            onClick={
              noFullAccess
                ? () => {
                    console.log(openAddOnsStoreModal, HOMETRACK_ADDON_ROUTE);
                    openAddOnsStoreModal({
                      path: HOMETRACK_ADDON_ROUTE,
                      logClickSource: "zoopla-preview",
                    });
                  }
                : undefined
            }
          />
        </PriceSection>
      )}
    </div>
  );
};

function PriceSection({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <ComparableDetailSection title={title}>
      <DataPanel.Grid columns={2} className="w-full">
        {children}
      </DataPanel.Grid>
    </ComparableDetailSection>
  );
}

const SkeletonPriceSection = () => {
  const { t } = useTranslation();

  return (
    <div className="relative w-full flex flex-col gap-y-4">
      <PriceSection title={t("comparables.selection_details.property_values_section.sale")}>
        <div className="relative h-[100px] w-full">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
        <div className="relative h-[100px] w-full">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
      </PriceSection>
      <PriceSection title={t("comparables.selection_details.property_values_section.rent")}>
        <div className="relative h-[100px] w-full">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
        <div className="relative h-[100px] w-full">
          <SkeletonLoading rows={1} showPadding={false} />
        </div>
      </PriceSection>
    </div>
  );
};
